/**
 * Narrow writes into `exam.tasks`, so that work on different parts of the task tree does not have to
 * serialise.
 *
 * THE PROBLEM THIS EXISTS FOR. Every producer that touched the task tree used to persist the same way: read
 * the exam, mutate a copy of the whole `tasks` array in memory, and `$set` the whole array back. That is a
 * read-modify-write, so two producers running concurrently silently lose one another's work — whoever writes
 * second wins entirely. It forced every task-tree job onto one lock key, which meant the master-solution
 * generation for main task 1 blocked the one for main task 2 even though the two subtrees share nothing.
 *
 * Both helpers here write by IDENTITY (`_id` through `arrayFilters`), never by array index. That matters for
 * more than tidiness: an index-based path silently writes to the WRONG task if the array is reordered between
 * the read and the write, whereas an identity-based one matches nothing and is merely lost — and lost is
 * recoverable, wrong is not.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../exam.js';
import type { NestedExamTask } from './tree-generation-utils.js';

/**
 * Persists ONE top-level main task's subtree, writing only the fields of `artifact` — so two roots of one
 * exam can be generated concurrently, AND a root's generation cannot revert an extraction that is writing the
 * other artifact at the same time.
 *
 * Scoped by FIELD as well as by root on purpose. Replacing the whole `tasks.$[root]` element would be enough
 * to keep two roots apart, and was the first thing tried — but the element carries both artifacts, so writing
 * all of it would have made a master-solution generation clobber the grading scheme that a concurrent
 * extraction had just written into the same subtree. Narrowing to the artifact removes the whole class.
 *
 * A root whose subtree carries no `_id` chain cannot be addressed and is skipped — see `persistTaskArtifact`.
 */
export async function persistRootSubtree(
  examDocId: string,
  root: NestedExamTask,
  artifact: TaskArtifact,
): Promise<string[]> {
  const { written, skipped, changedTaskIds } = await persistTaskArtifact(examDocId, [root], artifact);
  if (written === 0) {
    // Survivable and worth seeing: the root was removed by a structure rebuild, it carries no `_id`, or the
    // generation happened to produce no change.
    logger.warn(
      { examDocId, rootId: String((root as any)?._id ?? ''), artifact, skipped },
      'Persisting a root subtree modified nothing',
    );
  }
  return changedTaskIds;
}

/**
 * Fields a producer OWNS on a task. Extraction of an uploaded master solution and extraction of an uploaded
 * grading scheme both rebuild the entire tree in memory, but each only has anything to say about its own
 * artifact — so each writes only these paths and the two can run at the same time.
 *
 * `declaredMaxPoints` belongs to the grading scheme rather than to points-in-general: it is written only by
 * `dropDistributedParentTotals`, which runs precisely when a grading scheme makes parent totals derived.
 */
const OWNED_FIELDS = {
  masterSolution: ['masterSolution'],
  gradingScheme: ['gradingScheme', 'declaredMaxPoints'],
} as const;

export type TaskArtifact = keyof typeof OWNED_FIELDS;

interface FlatTask {
  /** `_id` of each ancestor from the root down to and including this node. */
  idPath: string[];
  node: any;
}

interface FlatTree {
  /** Every node this write can address. */
  tasks: FlatTask[];
  /**
   * Nodes no write can address, labelled by `taskNumber` — reported rather than returned, see the warning in
   * `persistTaskArtifact`.
   */
  unaddressable: string[];
}

/** How an unaddressable node is named in the warning. It has no `_id`, so `taskNumber` is all there is. */
const labelOf = (node: any): string => {
  const number = String(node?.taskNumber ?? '').trim();
  return number || '<no taskNumber>';
};

/**
 * Every node of the tree with the `_id` chain that addresses it, and a list of the ones that have none.
 *
 * A NODE WITHOUT AN `_id` TAKES ITS WHOLE SUBTREE WITH IT, and that is a property of the update path rather than a
 * choice: `tasks.$[t0].subTasks.$[t1]…` needs one `_id` per level, so a descendant of an unaddressable node cannot
 * be addressed either, however many ids of its own it carries. Recursing past it would find nodes it then could not
 * write. What was wrong before was not the skip but the SILENCE — the loss is now named, at every level.
 */
function flattenWithIdPath(nodes: any[], parentIds: string[], out: FlatTree): void {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    const id = String(node?._id ?? '').trim();
    if (!id) {
      out.unaddressable.push(labelOf(node));
      // Its descendants are unaddressable too, and counting them is what makes the warning say how much was
      // lost rather than merely that something was.
      collectSubtreeLabels(node?.subTasks, out.unaddressable);
      continue;
    }
    const idPath = [...parentIds, id];
    out.tasks.push({ idPath, node });
    if (Array.isArray(node?.subTasks) && node.subTasks.length > 0) flattenWithIdPath(node.subTasks, idPath, out);
  }
}

/** Labels for a subtree that is being skipped whole. */
function collectSubtreeLabels(nodes: any, out: string[]): void {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    out.push(labelOf(node));
    collectSubtreeLabels(node?.subTasks, out);
  }
}

/**
 * Writes ONLY the fields belonging to `artifact`, for every task in `tasks`, in one bulk operation.
 *
 * This is what lets master-solution and grading-scheme extraction run concurrently. They both produce a fully
 * rebuilt tree, and writing that tree is what made them conflict — not the work itself. Their field sets are
 * disjoint (see `OWNED_FIELDS`), so once each writes only its own, neither can clobber the other.
 *
 * WHAT IT CANNOT DO, deliberately: change the SHAPE of the tree. Adding, removing or reordering tasks is the
 * structure step's job, and a producer that needed to would be doing structure work under a lock that does
 * not exclude it. Nodes that carry no `_id` chain are skipped for the same reason — there is no identity to
 * address them by, and inventing one by position is the failure mode this file exists to avoid.
 */
export interface PersistArtifactResult {
  written: number;
  skipped: number;
  /**
   * `_id` of every task whose artifact fields this write actually CHANGED — as distinct from FILLED.
   *
   * A task whose artifact was empty and now has one was CREATED, and a creation is not a change: there is no
   * older version of anything for a downstream consumer to bring back into line. Every consumer of this list
   * is a "something that already existed is now stale" chain — a scheme written against a superseded master
   * solution, grades computed against a superseded scheme — and none of them has anything to do on a first
   * delivery. Reporting creations made the first extraction of an exam refine a grading scheme that had just
   * been read from the teacher's own upload.
   *
   * The point of computing it here rather than downstream: this is the one layer every producer of these
   * fields goes through — upload extraction, generation, refine — so a diff taken here is complete by
   * construction, and no caller can forget to report one.
   */
  changedTaskIds: string[];
}

/** Ignored when comparing: neither affects grading, and a regenerated entity gets a fresh `_id` every time. */
const IDENTITY_FIELDS = new Set(['_id', '_class']);

/**
 * A value's content, key-order independent, for comparison only.
 *
 * Key order matters because the two sides come from different places — one freshly built in memory, one read
 * back from Mongo — and an unstable comparison would report every task as changed on every upload, which is
 * exactly the "regrade everything" this exists to avoid.
 */
function contentKey(value: unknown): unknown {
  // Both, deliberately: an absent field and an explicitly null one are the same 'no content' to a diff.
  if (value === null || value === undefined) return null;
  if (typeof (value as { toObject?: unknown }).toObject === 'function') {
    return contentKey((value as { toObject: () => unknown }).toObject());
  }
  if (Array.isArray(value)) return value.map(contentKey);
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      if (IDENTITY_FIELDS.has(key)) continue;
      out[key] = contentKey((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}

/**
 * Writes ONLY the fields belonging to `artifact`, for every task in `tasks` whose value actually changed.
 *
 * This is what lets master-solution and grading-scheme extraction run concurrently. They both produce a fully
 * rebuilt tree, and writing that tree is what made them conflict — not the work itself. Their field sets are
 * disjoint (see `OWNED_FIELDS`), so once each writes only its own, neither can clobber the other.
 *
 * UNCHANGED TASKS ARE NOT WRITTEN, and the ids of the changed ones come back. A re-upload rebuilds the whole
 * tree even when the teacher only corrected task 3 — every other task comes out byte-identical, either
 * because nothing new was found for it (the step carries the old value forward) or because what was found
 * matches. Skipping those writes is a nicety; knowing WHICH ones differ is the point, because it is what lets
 * a re-upload regrade task 3 instead of the whole exam.
 *
 * THREE OUTCOMES PER TASK, not two, and the third is the one that matters: identical (not written), FILLED
 * (written, not reported — the field was empty and now is not), and CHANGED (written and reported). See
 * `changedTaskIds`.
 *
 * The comparison errs toward "changed" WITHIN that split: an unrecognised shape compares unequal, so a task
 * that already had content and whose new content cannot be understood is reported rather than skipped, and the
 * failure mode is an unnecessary regrade rather than a missed one.
 *
 * WHAT IT CANNOT DO, deliberately: change the SHAPE of the tree. Adding, removing or reordering tasks is the
 * structure step's job, and a producer that needed to would be doing structure work under a lock that does
 * not exclude it. Nodes that carry no `_id` chain are skipped for the same reason — there is no identity to
 * address them by, and inventing one by position is the failure mode this file exists to avoid. They are
 * WARNED ABOUT rather than skipped quietly: the write is the last place that can still see the difference
 * between "nothing to say about this task" and "could not say it".
 */
export async function persistTaskArtifact(
  examId: string,
  tasks: any[],
  artifact: TaskArtifact,
): Promise<PersistArtifactResult> {
  const flat: FlatTree = { tasks: [], unaddressable: [] };
  flattenWithIdPath(
    (Array.isArray(tasks) ? tasks : []).map(task => (typeof task?.toObject === 'function' ? task.toObject() : task)),
    [],
    flat,
  );

  // LOUD, because this is silent data loss otherwise: the producer computed an artifact for these tasks and it is
  // being thrown away. Only an `_id` can address a task (see `flattenWithIdPath`), so there is nothing to do about
  // it here — but a warning naming the tasks is the difference between a diagnosable bug and a teacher reporting
  // that one subtask never got a solution.
  if (flat.unaddressable.length > 0) {
    logger.warn(
      { examId, artifact, tasks: flat.unaddressable, count: flat.unaddressable.length },
      'Tasks carry no _id — their artifact fields, and their whole subtrees, were NOT written',
    );
  }
  if (flat.tasks.length === 0) return { written: 0, skipped: 0, changedTaskIds: [] };

  const fields = OWNED_FIELDS[artifact];
  const examCollection = await getCollection(Exam as any);

  // The stored values, to diff against. One read for the whole tree — the alternative, letting Mongo report
  // it via `modifiedCount`, cannot say WHICH ops modified anything, only how many.
  const stored = await examCollection.findOne({ _id: examId } as any, { projection: { tasks: 1 } });
  const storedFlat: FlatTree = { tasks: [], unaddressable: [] };
  flattenWithIdPath((stored as any)?.tasks ?? [], [], storedFlat);
  const storedById = new Map(storedFlat.tasks.map(entry => [entry.idPath[entry.idPath.length - 1], entry.node]));

  const changedTaskIds: string[] = [];
  const operations: Record<string, unknown>[] = [];

  for (const { idPath, node } of flat.tasks) {
    // `flattenWithIdPath` only ever emits a non-empty path, so the last element is always present.
    const taskId = idPath[idPath.length - 1] ?? '';
    if (!taskId) continue;
    const before = storedById.get(taskId);
    // Per field, so "which fields differ" and "did those fields hold anything before" are both answerable.
    // `contentKey` maps absent to `null`, and a task missing from `stored` entirely reads as absent for every
    // field — which is the right answer for it: a node this write cannot even address did not exist before.
    const diffs = fields
      .map(field => ({
        before: JSON.stringify(contentKey(before?.[field])),
        after: JSON.stringify(contentKey(node?.[field])),
      }))
      .filter(diff => diff.before !== diff.after);
    if (diffs.length === 0) continue;

    // FILLED, not changed, when every field that differs was empty before. Reported only otherwise — see
    // `changedTaskIds` for why a creation must not travel downstream as a change.
    if (diffs.some(diff => diff.before !== 'null')) changedTaskIds.push(taskId);

    // tasks.$[t0].subTasks.$[t1]… — one filter identifier per level, so depth is expressed in the path
    // rather than assumed.
    const filterIds = idPath.map((_, depth) => `t${depth}`);
    const path = filterIds.map((name, depth) => (depth === 0 ? `tasks.$[${name}]` : `subTasks.$[${name}]`)).join('.');

    const set: Record<string, unknown> = {};
    for (const field of fields) set[`${path}.${field}`] = node?.[field] ?? null;

    operations.push({
      updateOne: {
        filter: { _id: examId } as any,
        update: { $set: set } as any,
        arrayFilters: idPath.map((id, depth) => ({ [`${filterIds[depth]}._id`]: id })),
      },
    });
  }

  if (operations.length === 0) {
    logger.info({ examId, artifact, tasks: flat.tasks.length }, 'Persisted task artifact fields — nothing changed');
    return { written: 0, skipped: flat.tasks.length, changedTaskIds: [] };
  }

  const result = await (examCollection as any).bulkWrite(operations, { ordered: false });
  const written = Number(result?.modifiedCount ?? 0);
  logger.info(
    { examId, artifact, tasks: flat.tasks.length, changed: changedTaskIds.length, written },
    'Persisted task artifact fields',
  );
  return { written, skipped: flat.tasks.length - written, changedTaskIds };
}
